import { useState } from 'react';

import { Button } from '@/components/form/baseInputs/Button';
import { Link01Icon } from '@/components/icons/Link01Icon';
import { useTrackUserEvent } from '@/hooks/useTrackUserEvent';
import { useAssetProviderDataLinkingState } from '@/modules/assetProviderIntegrations/shared/hooks/useEnabledAssetsIntegrations';
import { IntegrationConnectionWarningModal } from '@/modules/estate/components/IntegrationConnectionWarningModal';
import { useHouseholdDetailsContext } from '@/modules/household/contexts/householdDetails.context';
import { useMigrateLegacyLinkDataParams } from '@/modules/household/LinkHouseholdDataTrowser/hooks/useMigrateLegacyLinkDataParams';
import { ManageIntegrationDataModal } from '@/modules/household/ManageIntegrationDataModal/ManageIntegrationDataModal';
import { ManageIntegrationDataPane } from '@/modules/household/ManageIntegrationDataModal/ManageIntegrationDataModal.types';
import { useTrowser } from '@/modules/navigation/trowsers/trowser.context';
import { TrowserKey } from '@/modules/navigation/trowsers/trowser.types';

export function DataLinkButton() {
  const { householdId, householdIntegrationLink } =
    useHouseholdDetailsContext();
  const { integrationsAvailableForDataLinking, canConnectToIntegrations } =
    useAssetProviderDataLinkingState();
  const trackUserEvent = useTrackUserEvent();
  const [
    isIntegrationConnectionWarningModalOpen,
    setIsIntegrationConnectionWarningModalOpen,
  ] = useState(false);

  const linkDataTrowser = useTrowser(TrowserKey.LinkHouseholdData);
  const isLinkDataModalOpen = linkDataTrowser.isOpen;
  // Translate a pre-trowser link (linkDataModal / linkDataInitialStep) into
  // today's params before anything reads them.
  useMigrateLegacyLinkDataParams();

  const handleOpenModal = () => {
    trackUserEvent('integration link_data_clicked');

    // If no provider data is available, prompt customer users to connect one.
    // Internal users can use tenant-synced data without a personal connection.
    if (integrationsAvailableForDataLinking.length === 0) {
      setIsIntegrationConnectionWarningModalOpen(true);
      return;
    }

    linkDataTrowser.open();
  };

  if (!householdId) {
    return null;
  }

  // A linked household does not need to select integration clients again.
  const initialPane = householdIntegrationLink
    ? ManageIntegrationDataPane.LinkTrusts
    : ManageIntegrationDataPane.SelectClients;

  return (
    <>
      <Button
        variant="secondary"
        size="sm"
        startIcon={Link01Icon}
        onClick={handleOpenModal}
      >
        Link data
      </Button>

      {isLinkDataModalOpen && (
        <ManageIntegrationDataModal
          isOpen
          onClose={linkDataTrowser.close}
          householdId={householdId}
          initialPane={initialPane}
        />
      )}

      <IntegrationConnectionWarningModal
        isOpen={isIntegrationConnectionWarningModalOpen}
        onClose={() => setIsIntegrationConnectionWarningModalOpen(false)}
        canConnectToIntegrations={canConnectToIntegrations}
      />
    </>
  );
}
